import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
              <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-file-list-3-line text-lg text-background-50"></i>
              </div>
              <span className={`font-semibold text-sm md:text-base whitespace-nowrap transition-colors ${
                scrolled ? 'text-foreground-950' : 'text-background-50'
              }`}>
                Kakobuy Spreadsheet Polska
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm whitespace-nowrap transition-colors hover:text-primary-500 ${
                  scrolled ? 'text-foreground-700' : 'text-background-50/90'
                }`}
              >
                <i className="ri-home-line mr-1"></i>
                Strona główna
              </Link>
              <Link
                to="/artykul/poradnik-zakupowy-kakobuy"
                className={`text-sm whitespace-nowrap transition-colors hover:text-primary-500 ${
                  scrolled ? 'text-foreground-700' : 'text-background-50/90'
                }`}
              >
                <i className="ri-shopping-bag-3-line mr-1"></i>
                Poradnik zakupowy
              </Link>
              <Link
                to="/artykul/kakobuy-spreadsheet-polski-przewodnik"
                className={`text-sm whitespace-nowrap transition-colors hover:text-primary-500 ${
                  scrolled ? 'text-foreground-700' : 'text-background-50/90'
                }`}
              >
                Przewodnik
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`text-sm whitespace-nowrap transition-colors hover:text-primary-500 ${
                  scrolled ? 'text-foreground-700' : 'text-background-50/90'
                }`}
              >
                Kakobuy Spreadsheet
              </a>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  scrolled
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    : 'bg-background-50 text-primary-500 hover:bg-background-100'
                }`}
              >
                Kup teraz
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>

              {/* Hamburger Button */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  scrolled ? 'text-foreground-950 hover:bg-background-200' : 'text-background-50 hover:bg-background-50/20'
                }`}
                aria-label="Menu"
              >
                <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-0 right-0 w-[300px] max-w-[85vw] h-full bg-background-50 shadow-lg overflow-y-auto animate-slide-in-right">
            <div className="p-5">
              {/* Close */}
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-foreground-950"></i>
                </button>
              </div>

              {/* Quick nav */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Nawigacja</h4>
                <div className="space-y-1">
                  <Link
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-950 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-home-line text-lg text-primary-500"></i>
                    Strona główna
                  </Link>
                  <Link
                    to="/artykul/poradnik-zakupowy-kakobuy"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-950 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-shopping-bag-3-line text-lg text-primary-500"></i>
                    Poradnik zakupowy
                  </Link>
                  <Link
                    to="/artykul/kakobuy-spreadsheet-polski-przewodnik"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-950 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-article-line text-lg text-primary-500"></i>
                    Przewodnik
                  </Link>
                  <Link
                    to="/artykul/bezpieczenstwo-kakobuy-poradnik"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-950 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-shield-check-line text-lg text-primary-500"></i>
                    Bezpieczeństwo
                  </Link>
                </div>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Kategorie produktów</h4>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                        <i className={`${cat.icon} text-secondary-500 text-sm`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.namePl}</span>
                      <span className="ml-auto text-xs text-foreground-500">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Articles */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Artykuły SEO</h4>
                <div className="space-y-0.5">
                  {[
                    { slug: 'najlepsze-sneakers-kakobuy-spreadsheet', label: 'Najlepsze Sneakersy' },
                    { slug: 'kakobuy-dla-poczatkujacych-poradnik', label: 'Dla początkujących' },
                    { slug: 'kakobuy-spreadsheet-2026-ranking', label: 'Ranking 2026' },
                    { slug: 'ukryte-produkty-kakobuy', label: 'Ukryte produkty' },
                    { slug: 'kakobuy-vs-alternatywy', label: 'Kakobuy vs Alternatywy' },
                    { slug: 'najlepsze-kakobuy-finds-2026', label: 'Najlepsze Finds' },
                    { slug: 'porownanie-produktow-kakobuy', label: 'Porównanie produktów' },
                  ].map((item) => (
                    <Link
                      key={item.slug}
                      to={`/artykul/${item.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-950 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-file-text-line text-accent-500"></i>
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all duration-200 cursor-pointer"
              >
                <i className="ri-fire-line"></i>
                Kup teraz
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}